import { msalPublicClientApplication } from "../../core/auth/useAuth";
import { tokenScopes } from "@coworker/apprestructured/src/assets/auth/msalConfig";
import {
  AccountInfo,
  InteractionRequiredAuthError,
  BrowserAuthError,
} from "@azure/msal-browser";
import { reportMessageToSentry } from "../useConfigureSentry";

const getMsalToken = async () => {
  const msalInstance = await msalPublicClientApplication;
  const accounts = msalInstance.getAllAccounts();

  const isCacheCorruptionError = (msg: string) =>
    msg.includes("Unexpected token") ||
    msg.includes("JSON") ||
    msg.includes("cache") ||
    msg.includes("parsing") ||
    msg.includes("unable_to_parse") ||
    msg.includes("crypto_key_not_found") ||
    msg.includes("key_not_found");

  if (!accounts[0]) {
    try {
      await msalInstance.acquireTokenRedirect({
        scopes: tokenScopes,
      });
      return null;
    } catch (error) {
      if (
        error instanceof BrowserAuthError &&
        error.errorCode === "interaction_in_progress"
      ) {
        return null;
      } else {
        reportMessageToSentry(`Error during acquireTokenRedirect.`, {
          level: "warning",
          extra: { accounts },
        });
        throw error;
      }
    }
  } else {
    try {
      const response = await msalInstance.acquireTokenSilent({
        scopes: tokenScopes,
        account: accounts[0] as AccountInfo,
      });
      sessionStorage.removeItem("msal.redirected");
      return response.accessToken;
    } catch (error: any) {
      const message = error?.message || "";

      // Check and log to sentry if MSAL cache corruption, no action required atm.
      if (isCacheCorruptionError(message)) {
        reportMessageToSentry(`MSAL cache corruption detected`, {
          level: "error",
          extra: { accounts, error: message },
        });
        // Just return null for now, no force redirect
        return null;
      }

      // Only log refresh token expiry
      if (
        message.includes("AADSTS70008") ||
        message.includes("refresh token has expired") ||
        message.includes("inactivity")
      ) {
        reportMessageToSentry(`MSAL refresh token expired due to inactivity`, {
          level: "error",
          extra: { accounts, error: message },
        });
        // User can manually login
        return null;
      }

      // Only do interactive login if explicitly required
      if (error instanceof InteractionRequiredAuthError) {
        if (sessionStorage.getItem("msal.redirected") === "1") {
          reportMessageToSentry(
            "MSAL redirect loop detected — skipping redirect",
            {
              level: "error",
              extra: { accounts, error: error.message },
            }
          );
          return null;
        }
        sessionStorage.setItem("msal.redirected", "1");
        try {
          await msalInstance.acquireTokenRedirect({
            scopes: tokenScopes,
          });
          return null;
        } catch (loginError) {
          reportMessageToSentry(
            `Catch block, error during InteractionRequiredAuthError`,
            {
              level: "warning",
              extra: { accounts: accounts[0], error: loginError },
            }
          );
          return null;
        }
      } else {
        reportMessageToSentry(`Token acquisition failed after all tries`, {
          level: "warning",
          extra: { accounts: accounts[0], error },
        });
        return null;
      }
    }
  }
};

export async function getTokenAsync() {
  return await getMsalToken();
}
